<template>
  <AppContainer :is-loading>
    <div>
      <EPageTitleMobile
        :title="t(useTranslationPath(callerPath, 'title'))"
        :description="t(useTranslationPath(callerPath, 'description'))"
      />

      <div class="space-y-8">
        <div v-if="hasVerificationMethods" class="space-y-2">
          <EForm>
            <EFormField
              id="user"
              :label="t(useTranslationPath(callerPath, 'label'))"
              :hint="t(useTranslationPath(callerPath, 'hint'))"
            >
              <ERadioGroup v-model="v$.selectedMethod.$model">
                <ERadio
                  v-if="email && emailEnabled"
                  id="email"
                  name="email"
                  value="email"
                  :label="email"
                />
                <ERadio
                  v-if="mobile && mobileEnabled"
                  id="sms"
                  name="sms"
                  value="sms"
                  :label="mobile"
                />
              </ERadioGroup>
            </EFormField>
          </EForm>
        </div>

        <EButton
          v-if="canAddMethod"
          :label="t(useTranslationPath(callerPath, 'addNew'))"
          variant="link"
          block
          @click="onAddVerificationMethod"
        />
      </div>
    </div>

    <template #bottom>
      <EButton
        :label="t(useTranslationPath(callerPath, 'next'))"
        :disabled="v$.$invalid"
        block
        @click="onSubmit"
      />
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useApiErrorHandler } from '@/composables/useApiErrorHandler.js'
import { useApiRequestHelper } from '@/composables/useApiRequestHelper.js'
import { useGetConfigVar, useGetVerificationMethods } from '@/composables/useGetConfigVar.js'
import { useGoBack } from '@/composables/useGoBack.js'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { useAppStore } from '@/stores/AppStore.js'
import { useUnauthenticatedTokenStore } from '@/stores/unauthenticated/UnauthenticatedTokenStore.js'
import { EButton, EForm, EFormField, EPageTitleMobile, ERadio, ERadioGroup } from '@expansivefm/eds'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { computed, inject, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const axios = inject('$axios')
const $apiUnauthenticatedRequest = inject('$apiUnauthenticatedRequest')

const { t } = useI18n()
const { apiUrlUnauthenticated } = useApiRequestHelper()
const { setCustomGoBack, resetGoBack } = useGoBack()
const router = useRouter()
const { handleErrors } = useApiErrorHandler(router)

const unauthenticatedTokenStore = useUnauthenticatedTokenStore()
const appStore = useAppStore()

const callerPath = 'views.unauthenticated.verification.methods'

const isLoading = ref(null)
const user = ref(null)
const availableMethods = ref([])

const form = reactive({
  selectedMethod: null,
})

const validationRules = {
  selectedMethod: { required },
}

const v$ = useVuelidate(validationRules, form)

onMounted(async () => {
  appStore.$patch({ showBackButton: false })
  isLoading.value = true

  await fetchUser()
  availableMethods.value = useGetVerificationMethods()

  const featureFlag = useGetConfigVar('ENGINEER_APP_FORCE_VERIFICATION_METHOD_ON_SIGN_IN')

  if (!featureFlag) {
    setCustomGoBack(() =>
      router.push({
        name: 'auth.verification.optional',
      }),
    )
  } else {
    setCustomGoBack(() =>
      router.push({
        name: 'auth.user',
      }),
    )
  }

  isLoading.value = false
  appStore.$patch({ showBackButton: true })
})

onUnmounted(async () => {
  resetGoBack()
})

const fetchUser = async () => {
  const userId = unauthenticatedTokenStore.engineerCheckInAccessToken?.user?.id

  try {
    return await $apiUnauthenticatedRequest
      .get('users', $apiUnauthenticatedRequest.configs.get.setId(userId))
      .then(({ data }) => {
        user.value = data
      })
  } catch (error) {
    await handleErrors(error)
  }
}

const emailEnabled = computed(() => {
  return availableMethods.value.includes('EMAIL')
})

const mobileEnabled = computed(() => {
  return availableMethods.value.includes('SMS')
})

const email = computed(() => {
  return user.value?.email
})

const mobile = computed(() => {
  return user.value?.mobile
})

const canAddMethod = computed(() => {
  return (!email.value && emailEnabled.value) || (!mobile.value && mobileEnabled.value)
})

const hasVerificationMethods = computed(() => {
  return (email.value && emailEnabled.value) || (mobile.value && mobileEnabled.value)
})

const onAddVerificationMethod = async () => {
  await router.push({
    name: 'auth.verification.methods.add',
  })
}

const onSubmit = async () => {
  v$.value.$touch()

  if (v$.value.$invalid) {
    return
  }

  isLoading.value = true

  try {
    await axios
      .get(`${apiUrlUnauthenticated.value}/login-link/${form.selectedMethod}`)
      .then(({ data }) => {
        return data.data
      })

    await unauthenticatedTokenStore.$patch({
      sentTo: form.selectedMethod === 'email' ? email.value : mobile.value,
    })

    await router.push({
      name: 'auth.verification.sent',
    })
  } catch (error) {
    await handleErrors(error)
  } finally {
    isLoading.value = false
  }
}
</script>
